import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

const FIELDS = [
  { name: "brand_name", label: "Nome da marca" },
  { name: "tagline", label: "Tagline (topo do hero)" },
  { name: "hero_line_1", label: "Hero — linha 1" },
  { name: "hero_line_2", label: "Hero — linha 2 (dourada)" },
  { name: "hero_subtitle", label: "Hero — subtítulo", textarea: true },
  { name: "marquee_text", label: "Texto do letreiro (separado por ✦)" },
  { name: "about_title", label: "Sobre — título" },
  { name: "about_text", label: "Sobre — texto", textarea: true },
  { name: "newsletter_title", label: "Newsletter — título" },
  { name: "newsletter_text", label: "Newsletter — texto", textarea: true },
  { name: "community_title", label: "Comunidade — título" },
  { name: "community_text", label: "Comunidade — texto", textarea: true },
  { name: "instagram_url", label: "URL do Instagram" },
  { name: "whatsapp", label: "WhatsApp (somente números, com DDI e DDD)" },
];

export default function AdminContent() {
  const { site, setSite } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setForm(r.data)).catch(() => setForm(site));
  }, []); // eslint-disable-line

  if (!form) return <p className="font-serif text-zinc-500">Carregando…</p>;

  const setField = (name, value) => setForm({ ...form, [name]: value });
  const setManifesto = (i, key, value) => {
    const manifesto = form.manifesto.map((c, idx) => (idx === i ? { ...c, [key]: value } : c));
    setForm({ ...form, manifesto });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", form);
      setSite(data);
      toast.success("Conteúdo do site atualizado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-content-form">
      <p className="mb-8 text-sm text-zinc-500">
        Edite os textos do site. As mudanças aparecem imediatamente na página inicial, contato e página de links.
      </p>
      <div className="flex flex-col gap-6">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-gold">{f.label}</label>
            {f.textarea ? (
              <textarea rows={3} value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="input-dark resize-none" data-testid={`content-field-${f.name}`} />
            ) : (
              <input value={form[f.name] || ""} onChange={(e) => setField(f.name, e.target.value)} className="input-dark" data-testid={`content-field-${f.name}`} />
            )}
          </div>
        ))}

        <div className="border border-gold/15 p-6">
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-gold">Manifesto — 3 capítulos</p>
          {(form.manifesto || []).map((chapter, i) => (
            <div key={i} className="mb-6 grid gap-4 sm:grid-cols-3">
              <input value={chapter.title} onChange={(e) => setManifesto(i, "title", e.target.value)} className="input-dark" placeholder={`Capítulo ${i + 1} — título`} data-testid={`manifesto-title-${i}`} />
              <textarea rows={2} value={chapter.text} onChange={(e) => setManifesto(i, "text", e.target.value)} className="input-dark resize-none sm:col-span-2" placeholder="Texto do capítulo" data-testid={`manifesto-text-${i}`} />
            </div>
          ))}
        </div>

        <button onClick={save} disabled={saving} className="btn-gold w-fit" data-testid="content-save-button">
          <Save size={14} /> {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </div>
    </div>
  );
}
